import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReportConfigurationLookupOption } from "../types";

interface ValuePickerProps {
    ariaLabel?: string;
    options: ReportConfigurationLookupOption[];
    optionGroupLabels?: Record<string, string>;
    onPick: (value: string) => void;
    placeholder?: string;
    mode?: "add" | "select";
    searchPlaceholder?: string;
    selectedCount?: number;
    totalCount?: number;
    selectedValues?: string[];
    // Values that are shown selected but cannot be toggled off (or on again) - the metric
    // client picker uses this for the report plan's own client, which is always in scope.
    // Locked values are still passed in selectedValues; this only governs interaction.
    lockedValues?: string[];
    onToggle?: (value: string) => void;
    onPickMany?: (values: string[]) => void;
    onClear?: () => void;
    disabled?: boolean;
    searchable?: boolean;
    searchThreshold?: number;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /**
     * Lets the user add the text typed in the search box as a value of its own ("Use ..."),
     * for columns with no lookup list - a calculated field's values are worked out per row,
     * so there is nothing to list. The search box is then always shown and the picker is
     * never disabled for having no options.
     */
    allowCustom?: boolean;
}

export function ValuePicker({
    ariaLabel,
    options,
    optionGroupLabels,
    onPick,
    placeholder = "+ Add Value...",
    mode = "add",
    searchPlaceholder = "Search Values...",
    selectedCount,
    totalCount,
    selectedValues,
    lockedValues,
    onToggle,
    onPickMany,
    onClear,
    disabled,
    searchable,
    searchThreshold = 8,
    open: controlledOpen,
    onOpenChange,
    allowCustom,
}: ValuePickerProps) {
    const pickerRef = useRef<HTMLDivElement>(null);
    const [internalOpen, setInternalOpen] = useState(false);
    const [query, setQuery] = useState("");
    const open = controlledOpen ?? internalOpen;
    const isMulti = Boolean(onToggle || onPickMany || onClear);
    const showSearch = allowCustom || (searchable ?? options.length > searchThreshold);
    const selectedSet = useMemo(() => new Set(selectedValues || []), [selectedValues]);
    const lockedSet = useMemo(() => new Set(lockedValues || []), [lockedValues]);
    const filteredOptions = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        if (!normalized) return options;
        return options.filter((option) =>
            option.name.toLowerCase().includes(normalized)
            || option.value.toLowerCase().includes(normalized));
    }, [options, query]);

    const setOpen = (nextOpen: boolean) => {
        if (controlledOpen === undefined) {
            setInternalOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
    };

    useEffect(() => {
        if (!open) {
            setQuery("");
            return undefined;
        }

        const closeIfOutside = (event: MouseEvent) => {
            if (!pickerRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", closeIfOutside);
        return () => document.removeEventListener("mousedown", closeIfOutside);
    }, [open, controlledOpen, onOpenChange]);

    const pick = (value: string) => {
        onPick(value);
        setQuery("");
        if (!isMulti) setOpen(false);
    };

    const customValue = allowCustom ? query.trim() : "";
    const offerCustom = !!customValue && !options.some((option) => option.value === customValue || option.name.toLowerCase() === customValue.toLowerCase());

    const pickMany = () => {
        if (!onPickMany || !filteredOptions.length) return;
        onPickMany(filteredOptions.map((option) => option.value));
        setQuery("");
    };

    return (
        <div className={`rc-value-picker ${mode === "select" ? "select-mode" : "add-mode"} ${open ? "open" : ""}`} ref={pickerRef} onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}>
            <button className="rc-value-picker-trigger" aria-label={ariaLabel} aria-expanded={open} disabled={disabled || (!options.length && !allowCustom)} type="button" onClick={() => setOpen(!open)}>
                <span className="rc-value-picker-main">
                    <span>{options.length || mode === "select" || allowCustom ? placeholder : "All Values Added"}</span>
                </span>
                <span className="material-icons md-18 rc-value-picker-caret" aria-hidden="true">keyboard_arrow_down</span>
            </button>
            {open && (
                <div className="rc-value-picker-panel">
                    {showSearch && (
                        <div className="rc-value-picker-search">
                            <span className="material-icons md-16" aria-hidden="true">search</span>
                            <input
                                autoFocus
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key !== "Enter" || !offerCustom) return;
                                    event.preventDefault();
                                    pick(customValue);
                                }}
                                placeholder={allowCustom ? "Search Or Type A Value..." : searchPlaceholder}
                            />
                        </div>
                    )}
                    {isMulti && (
                        <div className="rc-value-picker-tools">
                            <span>{selectedCount ?? selectedValues?.length ?? 0}{typeof totalCount === "number" ? ` Of ${totalCount}` : ""} Selected</span>
                            <div>
                                {onPickMany && <button className="rc-value-picker-tool" type="button" onClick={pickMany} disabled={!filteredOptions.length}><span className="material-icons md-14" aria-hidden="true">done_all</span>{query.trim() ? "Select Shown" : "Select All"}</button>}
                                {onClear && <button className="rc-value-picker-tool" type="button" onClick={onClear}><span className="material-icons md-14" aria-hidden="true">backspace</span>Clear</button>}
                            </div>
                        </div>
                    )}
                    <div className="rc-value-picker-list">
                        {offerCustom && (
                            <button className="rc-value-picker-option" type="button" onClick={() => pick(customValue)} title={`Use "${customValue}"`}>
                                <span className="material-icons md-16" aria-hidden="true">add</span>
                                <span>Use "{customValue}"</span>
                            </button>
                        )}
                        {filteredOptions.length ? filteredOptions.map((option, index) => {
                            const isSelected = selectedSet.has(option.value);
                            const isLocked = lockedSet.has(option.value);
                            const classNames = ["rc-value-picker-option", isSelected ? "selected" : "", isLocked ? "locked" : ""].filter(Boolean).join(" ");
                            const groupLabel = optionGroupLabels?.[option.value];
                            return (
                                <Fragment key={option.id}>
                                    {groupLabel && (index === 0 || optionGroupLabels?.[filteredOptions[index - 1].value] !== groupLabel) && (
                                        <div className="rc-value-picker-group-label">{groupLabel}</div>
                                    )}
                                    <button className={classNames} type="button" disabled={isLocked} aria-disabled={isLocked || undefined} onClick={() => { if (isLocked) return; if (onToggle) onToggle(option.value); else pick(option.value); }} title={option.name}>
                                        {isMulti && <span className={isSelected ? "rc-checkbox checked" : "rc-checkbox"} aria-hidden="true" />}
                                        <span>{option.name}</span>
                                    </button>
                                </Fragment>
                            );
                        }) : !offerCustom && <div className="rc-value-picker-empty">{allowCustom ? "Type A Value To Add It" : "No Matching Values"}</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
